/**
 * photoSync.js
 * Sobe para o Firebase Storage todas as fotos que estão na fila (IndexedDB)
 * e atualiza os documentos correspondentes no Firestore.
 *
 * Chamado:
 *  - Quando o app fica online (evento 'online')
 *  - Quando o usuário abre o app e está autenticado
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { storage, db, auth } from '../firebase';
import { getAllQueuedPhotos, dequeuePhoto } from './photoQueue';
import { remoteLog } from './remoteLog';

let syncRunning = false;

/**
 * Percorre recursivamente um objeto e substitui referências `{ idbKey, ... }`
 * por `{ url, legenda }` após upload para o Storage.
 * Retorna o objeto atualizado e a lista de keys que foram processadas.
 */
async function resolvePhotos(data, processedKeys, osId) {
  if (!data || typeof data !== 'object') return data;

  // Referência direta a uma foto pendente: { idbKey, legenda, url: null }
  if (data.idbKey) {
    const entry = await import('./photoQueue').then(m => m.getQueuedPhoto(data.idbKey));
    if (!entry) return { url: null, legenda: data.legenda || '' }; // perdida — não bloqueia
    const storRef = ref(storage, entry.storagePath);
    await uploadBytes(storRef, entry.blob, { contentType: 'image/jpeg' });
    const url = await getDownloadURL(storRef);
    processedKeys.push(data.idbKey);
    return { url, legenda: data.legenda || '' };
  }

  if (Array.isArray(data)) {
    return Promise.all(data.map(item => resolvePhotos(item, processedKeys, osId)));
  }

  // Objeto genérico — percorre as chaves
  const result = {};
  for (const [k, v] of Object.entries(data)) {
    // Preserva Timestamps do Firestore sem serializar
    if (v && typeof v === 'object' && typeof v.toDate === 'function') {
      result[k] = v;
    // Assinatura base64 pendente (Teste de Corante salvo offline)
    } else if (k === 'assinaturaCliente' && typeof v === 'string' && v.startsWith('data:')) {
      try {
        const resp  = await fetch(v);
        const blob  = await resp.blob();
        const storRef = ref(storage, `ordens/${auth.currentUser.uid}/${osId}/assinatura_cliente.png`);
        await uploadBytes(storRef, blob);
        result[k] = await getDownloadURL(storRef);
        processedKeys.push('assinatura_' + osId); // marcador para saber que processou
      } catch {
        result[k] = v; // mantém base64 em caso de erro, tenta no próximo sync
      }
    } else {
      result[k] = await resolvePhotos(v, processedKeys, osId);
    }
  }
  return result;
}

/**
 * Verifica se um objeto (recursivamente) tem alguma referência idbKey pendente
 * ou assinatura base64 não convertida.
 */
function hasPendingPhotos(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.idbKey) return true;
  if (Array.isArray(data)) return data.some(hasPendingPhotos);
  return Object.entries(data).some(([k, v]) => {
    if (k === 'assinaturaCliente' && typeof v === 'string' && v.startsWith('data:')) return true;
    return hasPendingPhotos(v);
  });
}

/**
 * Ponto de entrada principal.
 * Percorre todas as fotos na fila, agrupa por osId, e faz upload + update no Firestore.
 */
export async function syncPendingPhotos() {
  if (syncRunning) return;
  if (!auth.currentUser) return;
  if (!navigator.onLine) return;

  syncRunning = true;
  try {
    const queue = await getAllQueuedPhotos();
    if (queue.length === 0) return;

    remoteLog('info', 'photo_sync_start',
      `Iniciando sync de ${queue.length} foto(s) pendente(s)`, { count: queue.length });

    // Agrupa por osId
    const byOs = {};
    for (const entry of queue) {
      (byOs[entry.osId] = byOs[entry.osId] || []).push(entry);
    }

    let uploaded = 0;
    let errors   = 0;

    for (const osId of Object.keys(byOs)) {
      try {
        const snap = await getDoc(doc(db, 'ordens', osId));
        if (!snap.exists()) {
          // OS não existe mais — descarta as fotos
          for (const e of byOs[osId]) await dequeuePhoto(e.key);
          continue;
        }

        const dadosAtuais = snap.data().dadosEspecificos;
        if (!dadosAtuais || !hasPendingPhotos(dadosAtuais)) {
          // Documento já não tem pendências — limpa as keys órfãs
          for (const e of byOs[osId]) await dequeuePhoto(e.key);
          continue;
        }

        const processedKeys = [];
        const dadosResolvidos = await resolvePhotos(dadosAtuais, processedKeys, osId);

        await updateDoc(doc(db, 'ordens', osId), { dadosEspecificos: dadosResolvidos });

        // Remove as keys que foram processadas com sucesso
        for (const key of processedKeys) await dequeuePhoto(key);
        uploaded += processedKeys.length;

      } catch (err) {
        errors++;
        remoteLog('error', 'photo_sync_error',
          `Erro ao sincronizar fotos da OS ${osId}: ${err?.message}`,
          { osId, code: err?.code, message: err?.message });
      }
    }

    if (uploaded > 0) {
      remoteLog('info', 'photo_sync_done',
        `Sync concluído: ${uploaded} foto(s) enviada(s)`, { uploaded, errors });
    }
  } catch (err) {
    remoteLog('error', 'photo_sync_fatal',
      `Erro fatal no sync de fotos: ${err?.message}`, { message: err?.message });
  } finally {
    syncRunning = false;
  }
}

/**
 * Registra os listeners de `online` e `visibilitychange` para disparo automático.
 * Chame uma vez na inicialização do app.
 */
export function startPhotoSyncListeners() {
  const trySync = () => {
    if (navigator.onLine && auth.currentUser) {
      void syncPendingPhotos();
    }
  };
  window.addEventListener('online', trySync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trySync();
  });
  // Tenta imediatamente também
  trySync();
}
